import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { forkJoin, skip, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import { MobilePanSearchService } from '../../../services/mobile-pan/mobile-pan.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { DocumentExportService } from '../../../services/document-export/document-export.service';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-osint-mobile-pan-search',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    HeaderComponent,
    PaginatorComponent,
    SidebarComponent,
    RestrictedAccessWarningDirective, CreditsLabelPipe],
  templateUrl: './osint-mobile-pan-search.component.html',
  styleUrls: ['./osint-mobile-pan-search.component.css'],
})
export class OsintMobilePanSearchComponent implements OnInit, OnDestroy {
  accessDetails: any;
  availableCredits: number | null = null;
  creditsLoading = true;
  totalCredits: number = 0;
  singlePhone: string = '';

  textAreaInput: string = '';
  multiplePhones: string[] = [];
  extraPhones: Array<{ value: string }> = [];
  readonly maxSearchRows = 5;

  isMultipleSearch: boolean = false;

  allJobResultsSubscription?: Subscription;
  loadingJobIds: boolean = false;
  jobIds: string[] = [];

  private lastSearchTime: number = 0;
  private readonly SEARCH_THROTTLE_MS: number = 1000; // 1 second

  searchType: string = 'mobile_pan';
  loadingRecentResults: boolean = false;
  recentResults: any = [];
  recentResultsPaged: any = [];

  ipAddress: string = '';
  location: string = '';

  recentResultsCount: number = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage: boolean = false;
  recentResultsHasNextPage: boolean = false;

  recentResultsSelectAll = { isSelected: false };

  selectedFilterType: string = 'phone';
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;

  /** Indian mobile only — matches backend INDIAN_PHONE_PATTERN. */
  private static readonly INDIAN_PHONE_PATTERN = /^(\+91[\s-]?)?[6-9]\d{9}$/;

  private validatePhoneNumber(phone: string): boolean {
    const normalized = String(phone || '').trim().replace(/\s+/g, '');
    if (!normalized) return false;
    return OsintMobilePanSearchComponent.INDIAN_PHONE_PATTERN.test(normalized);
  }

  private indianPhoneErrorMessage(multiple: boolean): string {
    return multiple
      ? 'One or more numbers are invalid. Enter Indian mobile numbers only (10 digits starting with 6-9, optional +91).'
      : 'Enter a valid Indian mobile number (10 digits starting with 6-9, optional +91).';
  }

  filterTypes: any = [
    { label: 'Phone', value: 'phone' },
    // { label: 'Status', value: 'status' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];

  filterKey: string = '';
  filterValue: string = '';

  phoneFilter: string = '';
  statusFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );

    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((recentResultPaged: any) => recentResultPaged.isSelected)
      .map((recentResultPaged: any) => recentResultPaged.mobile_pan_search_id);
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private userAccountService: UserAccountService,
    private mobilePanSearchService: MobilePanSearchService,
    private searchPollingService: SearchPollingService,
    private utilsService: UtilsService,
    private documentExportService: DocumentExportService,
    private searchServiceStatusService: SearchServiceStatusService,
  ) {}

  ngOnInit(): void {
    this.accessDetails = this.retrieveAccessDetails();

    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = searchServices.mobile_pan_search === false;
      }
    });
    this.searchServiceStatusService.refreshSearchServices();

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'mobilePanSearchCreditCost',
      );
    });

    this.getRecentResults();
    this.getAvailableCredits();

    this.subscribeToJobResultsUpdates();

    // Check for auto-search from navigation
    const autoSearchState = history.state;
    if (autoSearchState?.autoSearch && autoSearchState?.searchQuery) {
      this.singlePhone = autoSearchState.searchQuery;
      // Clear the auto-search state to prevent re-triggering on back navigation
      history.replaceState(
        { ...history.state, autoSearch: false, searchQuery: null },
        '',
      );
      // Use setTimeout to ensure component is fully initialized
      setTimeout(() => {
        this.onSearchSinglePhoneButtonClick();
      }, 100);
    }
  }

  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();

    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        this.recentResultsPaged.forEach((result: any) => {
          if (result.job_id) {
            const updatedStatus = map.get(result.job_id);
            if (updatedStatus) {
              result.done = updatedStatus.done || false;
              result.status = updatedStatus.status || 'pending';
            }
          }
        });
      });
  }

  ngOnDestroy() {
    this.allJobResultsSubscription?.unsubscribe();
  }

  retrieveAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.accessDetails || null;
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        // console.log('GET USER PROFILE RESPONSE: ', response);

        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
        this.totalCredits = subscription?.total_credits;
      },
      error: (error) => {
        console.error(error);

        let errorMessage = 'Failed to load available credits';

        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }

        this.toastr.error(errorMessage, 'Credits Load Failed');
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
      complete: () => {},
    });
  }

  getRecentResults() {
    this.loadingRecentResults = true;

    this.mobilePanSearchService
      .getMobilePanSearchHistory(
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
      )
      .subscribe({
        next: (response) => {
          this.loadingRecentResults = false;

          this.recentResults = response?.data?.data_list;
          this.recentResultsPaged = this.addAdditionalKeys(this.recentResults);

          this.recentResultsCount = response?.data?.total_items;
          this.recentResultsHasPreviousPage = response?.data?.has_previous;
          this.recentResultsHasNextPage = response?.data?.has_next;

          this.refreshPendingJobStatuses();
        },
        error: (error) => {
          console.error(error);
          this.loadingRecentResults = false;

          let errorMessage = 'Failed to load recent search results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Load Results Failed');
        },
      });
  }

  addAdditionalKeys(recentResults: any) {
    return recentResults.map((recentResult: any) => ({
      ...recentResult,
      isSelected: false,
    }));
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateRecentResultPaged(isChecked, recentResultPaged.mobile_pan_search_id);
  }

  updateRecentResultPaged(isChecked: boolean, searchId: string) {
    const recentResultsPagedToUpdate = this.recentResultsPaged.find(
      (recentResultPaged: any) =>
        recentResultPaged.mobile_pan_search_id === searchId,
    );

    if (recentResultsPagedToUpdate) {
      recentResultsPagedToUpdate.isSelected = isChecked;
    }
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.recentResultsSelectAll.isSelected = false;

    this.getRecentResults();
  }

  onDeleteButtonClick() {
    this.deleteRecentResults();
  }

  deleteRecentResults() {
    this.mobilePanSearchService
      .deleteMobilePanSearchHistory({
        mobile_pan_search_ids: this.selectedRecentResults,
      })
      .subscribe({
        next: () => {
          this.recentResultsSelectAll.isSelected = false;

          this.updateRecentResultsPaged();
          this.updateRecentResultsCurrentPage();

          this.getRecentResults();
        },
        error: (error) => {
          console.error(error);

          let errorMessage = 'Failed to delete selected results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Delete Failed');
        },
      });
  }

  updateRecentResultsPaged() {
    this.recentResultsPaged = this.recentResultsPaged.filter(
      (recentResultPaged: any) =>
        !this.selectedRecentResults.includes(recentResultPaged.mobile_pan_search_id),
    );
  }

  updateRecentResultsCurrentPage() {
    if (this.recentResultsPaged.length === 0) {
      if (this.recentResultsHasNextPage) {
      } else {
        if (this.recentResultsHasPreviousPage) {
          this.recentResultsCurrentPage--;
        }
      }
    }
  }

  onFilterTypeChange() {
    this.reset();
  }

  reset() {
    this.recentResultsCurrentPage = 1;

    this.filterKey = '';
    this.filterValue = '';

    this.phoneFilter = '';
    this.statusFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
  }

  onMultipleSearchChange(): void {
    if (this.isMultipleSearch) {
      if (!this.textAreaInput.trim() && this.singlePhone.trim()) {
        this.textAreaInput = this.singlePhone.trim();
      }
      return;
    }
    if (!this.singlePhone.trim()) {
      const first = this.textAreaInput
        .split('\n')
        .map((value) => value.trim())
        .find((value) => value.length > 0);
      if (first) {
        this.singlePhone = first;
      }
    }
  }

  resetSearchInputs() {
    this.singlePhone = '';
    this.textAreaInput = '';
    this.multiplePhones = [];
    this.extraPhones = [];
    this.isMultipleSearch = false;
  }

  onSearchSinglePhoneButtonClick() {
    if (this.isServiceDisabled) {
      this.toastr.error('Mobile PAN search is currently unavailable');
      return;
    }
    const values = this.collectPhoneValues();
    if (!values.length) {
      this.toastr.error('Please enter a phone number');
      return;
    }
    if (values.length > this.maxSearchRows) {
      this.toastr.error('You can only search a maximum of 5 phone numbers at once.');
      return;
    }
    if (!values.every((phone) => this.validatePhone(phone))) {
      this.toastr.error(this.indianPhoneErrorMessage(values.length > 1));
      return;
    }
    if (this.isThrottled()) {
      return;
    }
    this.isMultipleSearch = values.length > 1;
    this.multiplePhones = values;
    this.getJobIds();
  }

  get canAddSearchRow(): boolean {
    return this.extraPhones.length < this.maxSearchRows - 1;
  }

  addSearchRow(): void {
    if (!this.canAddSearchRow) {
      this.toastr.error('You can only search a maximum of 5 phone numbers at once.');
      return;
    }
    this.extraPhones = [...this.extraPhones, { value: '' }];
    this.isMultipleSearch = this.extraPhones.length > 0;
  }

  removeSearchRow(index: number): void {
    this.extraPhones = this.extraPhones.filter((_, i) => i !== index);
    this.isMultipleSearch = this.extraPhones.length > 0;
  }

  private collectPhoneValues(): string[] {
    const rawValues = this.isMultipleSearch
      ? this.textAreaInput.split('\n')
      : [this.singlePhone];
    return rawValues
      .map((value) =>
        String(value || '')
          .trim()
          .replace(/\s+/g, '')
          ,
      )
      .filter((value) => value.length > 0);
  }

  private isThrottled(): boolean {
    const now = Date.now();
    const timeSinceLastSearch = now - this.lastSearchTime;

    return timeSinceLastSearch < this.SEARCH_THROTTLE_MS;
  }

  getJobIds() {
    const { ip, location } = this.retrieveIPAndLocation();
    const values = this.multiplePhones.length
      ? this.multiplePhones.map((e) =>
          e.trim().replace(/\s+/g, ''),
        )
      : [this.singlePhone.trim().replace(/\s+/g, '')];

    this.loadingJobIds = true;
    this.lastSearchTime = Date.now();

    const requests = values.map((phone) => {
      const payload: any = {
        phone,
        device: 'Chrome',
      };
      if (ip) payload.ip_address = ip;
      if (location) payload.location = location;
      return this.mobilePanSearchService.searchMobilePan(payload);
    });

    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        const jobIds: string[] = [];
        const bgJobs: Array<{
          job_id: string;
          searchType: string;
          label: string;
        }> = [];

        responses.forEach((response, index) => {
          const jobId = response?.data?.job_id;
          if (jobId) {
            jobIds.push(jobId);
            bgJobs.push({
              job_id: jobId,
              searchType: 'mobile_pan',
              label: String(values[index] ?? jobId),
            });
          }
        });

        this.jobIds = jobIds;

        if (jobIds.length) {
          this.searchPollingService.startPolling(this.jobIds, (ids) =>
            this.mobilePanSearchService.getSearchResults(ids),
          );
          this.searchPollingService.ensureBackgroundPolling(bgJobs, 3 * 1000);
          this.getAllJobResults();
        }

        this.reset();
        this.resetSearchInputs();
        this.getRecentResults();

        this.loadingJobIds = false;
      },
      error: (error) => {
        console.error(error);
        this.loadingJobIds = false;
        if (error?.status === 503) {
          this.isServiceDisabled = true;
        }

        let errorMessage = 'An error occurred while processing your request';

        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }

        this.toastr.error(errorMessage, 'Search Failed');
      },
    });
  }

  refreshPendingJobStatuses() {
    this.recentResultsPaged.forEach((result: any) => {
      if (result.job_id) {
        const cachedStatus = this.searchPollingService.getCachedJobStatus(
          result.job_id,
        );
        if (cachedStatus) {
          result.done = cachedStatus.done || false;
          result.status = cachedStatus.status || 'pending';
        }
      }
    });

    const pendingJobIds = this.recentResultsPaged
      .filter(
        (result: any) =>
          result.job_id &&
          (!result.done ||
            result.done === false ||
            result.status === 'pending'),
      )
      .map((result: any) => result.job_id)
      .filter((jobId: string) => !!jobId);

    if (pendingJobIds.length > 0) {
      this.searchPollingService
        .checkJobStatus(pendingJobIds, (jobIds) =>
          this.mobilePanSearchService.getSearchResults(jobIds),
        )
        .subscribe({
          next: (jobResultsMap) => {
            this.recentResultsPaged.forEach((result: any) => {
              if (result.job_id) {
                const updatedStatus = jobResultsMap.get(result.job_id);
                if (updatedStatus) {
                  result.done = updatedStatus.done || false;
                  result.status = updatedStatus.status || 'pending';
                }
              }
            });
          },
          error: (error) => {
            console.warn('Failed to refresh job statuses:', error);
          },
        });
    }
  }

  getAllJobResults() {}

  extractJobIds(jobs: any[]) {
    if (!jobs || !Array.isArray(jobs)) {
      return [];
    }

    if (jobs.length > 0 && typeof jobs[0] === 'string') {
      return jobs;
    }

    return jobs.filter((job) => job.done === false).map((job) => job.job_id);
  }

  /** Matches backend Indian mobile format (INDIAN_PHONE_PATTERN). */
  validatePhone(phone: string) {
    const normalized = phone.trim().replace(/\s+/g, '');
    return this.validatePhoneNumber(normalized);
  }

  extractMultiplePhones() {
    this.multiplePhones = this.textAreaInput
      .split('\n')
      .map((phone) => phone.trim().replace(/\s+/g, ''))
      .filter((phone) => phone.length > 0);
  }

  validatePhones() {
    return this.multiplePhones.every((phone) => this.validatePhone(phone));
  }

  onSearchMultiplePhonesButtonClick() {
    this.extractMultiplePhones();

    if (this.validatePhones()) {
      if (this.isThrottled()) {
        return;
      }
      this.getJobIds();
    } else {
      this.toastr.error(this.indianPhoneErrorMessage(true));
    }
  }

  goToSearchResultPage(searchResult: any) {
    this.router.navigate([`/mobile-pan-search-result/${searchResult.job_id}`], {
      state: { searchResult },
    });
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;

    this.recentResultsSelectAll.isSelected
      ? this.recentResultsPagedSelectAll()
      : this.recentResultsPagedDeselectAll();
  }

  recentResultsPagedSelectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: true,
      }),
    );
  }

  recentResultsPagedDeselectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: false,
      }),
    );
  }

  onPhoneFilterChange(phone: string) {
    const trimmedPhone = phone.trim();

    if (trimmedPhone === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedPhone.length === 1) {
      return;
    } else {
      this.filterKey = 'searched_phone';
      this.filterValue = trimmedPhone;

      this.getRecentResults();
    }
  }

  onStatusFilterChange(status: string) {
    this.statusFilter = status;

    if (this.statusFilter === 'all') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.statusFilter === 'success') {
      this.filterKey = 'status';
      this.filterValue = 'success';

      this.getRecentResults();
    }

    if (this.statusFilter === 'pending') {
      this.filterKey = 'status';
      this.filterValue = 'pending';

      this.getRecentResults();
    }
  }

  onNotesFilterChange(notes: string) {
    const trimmedNotes = notes.trim();

    if (trimmedNotes === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedNotes.length === 1) {
      return;
    } else {
      this.filterKey = 'notes';
      this.filterValue = trimmedNotes;

      this.getRecentResults();
    }
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onExportExcelButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToExcel(transformedRecentResultsPaged);
  }

  transformRecentResultsPaged() {
    return this.recentResultsPaged.map((recentResultPaged: any) => ({
      'MOBILE PAN SEARCH ID': recentResultPaged.mobile_pan_search_id,
      'JOB ID': recentResultPaged.job_id,
      'SEARCHED PHONE': recentResultPaged.searched_phone,
      'USER EMAIL': recentResultPaged.user?.email || '',
      'IP ADDRESS': recentResultPaged.ip_address,
      'LOCATION': recentResultPaged.location,
      'DEVICE': recentResultPaged.device,
      'NOTES':
        recentResultPaged.notes === undefined ? null : recentResultPaged.notes,
      'CREATED ON': this.utilsService.formatDateTime(
        recentResultPaged.created_on,
        'MMM d, y, h:mm:ss a',
      ),
    }));
  }

  exportToExcel(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `mobile-pan-results-${timestamp || 'export'}`;
    this.documentExportService.exportToExcel(
      transformedRecentResultsPaged,
      fileName,
      'Mobile PAN Results',
    );
  }

  onExportCsvButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToCsv(transformedRecentResultsPaged);
  }

  exportToCsv(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `mobile-pan-results-${timestamp || 'export'}`;
    this.documentExportService.exportToCsv(
      transformedRecentResultsPaged,
      fileName,
      'Mobile PAN Results',
    );
  }

  onExportPdfButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToPdf(transformedRecentResultsPaged);
  }

  exportToPdf(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `mobile-pan-results-${timestamp || 'export'}`;
    const title = `Mobile PAN results generated from Ryno`;
    const username = this.getUsername();

    this.documentExportService.exportResultsToPdf(
      transformedRecentResultsPaged,
      fileName,
      title,
      username,
    );
  }

  private getUsername(): string {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.loginDetails?.user_name || '';
  }

  private retrieveIPAndLocation(): { ip: string; location: string } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    const ipRaw = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';

    const ip = ipRaw.trim();
    const isValidIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);

    return {
      ip: isValidIPv4 ? ip : '',
      location,
    };
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  /** Truncate text to max words; if longer, append " ....." */
  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }
}
